// Plans (iOS FS-197, drawn for the web): a room at the head (Ruisdael's road through the fields) with
// the page's words on glass; invitations as plates; the plans you're reading as cards you can press,
// each with its cover, today's place and who is reading with you (presence, never a ranking); the
// catalogue as a shelf of covers, the library's plans and then your friends'; and the Bible you've
// read. Together plans show presence ("Ruth has read today"), never a count of who is ahead.
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Art } from "../../../components/Art";
import { Avatar } from "../../../components/Avatar";
import { AvatarStack } from "../../../components/AvatarStack";
import { Badge } from "../../../components/Badge";
import { BibleMap } from "../../../components/BibleMap";
import { Button, LinkButton } from "../../../components/Button";
import { EmptyState } from "../../../components/Empty";
import { Icon } from "../../../components/Icon";
import { PlanCover } from "../../../components/PlanCover";
import { SectionHeader } from "../../../components/SectionHeader";
import { Card, Plate } from "../../../components/Surface";
import ui from "../../../components/ui.module.css";
import { TickBar } from "../../../components/WeekStrip";
import { useInvitations, useJourneys, usePlans, useRespondInvitation } from "../../../data/planQueries";
import { atNoon, localDate, othersIn, readingsLine, type Invitation, type Journey, type PlanSummary } from "../../../data/plans";
import { useSession } from "../../../data/queries";
import { useTitle } from "../../../lib/useTitle";
import { motionStyles } from "../../../motion";
import s from "./plans.module.css";

export const Route = createFileRoute("/_app/plans/")({ component: PlansHub });

const fmt = (iso: string) => atNoon(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long" });
const first = (name: string) => name.split(" ")[0]!;
const names = (xs: string[]) => (xs.length < 3 ? xs.join(" and ") : `${xs.slice(0, -1).join(", ")} and ${xs.at(-1)}`);

function PlansHub() {
  useTitle("Plans");
  const { data: journeys = [] } = useJourneys();
  const { data: plans = [] } = usePlans();
  const { data: invitations = [] } = useInvitations();
  const { data: session } = useSession();
  const reading = new Set(journeys.map((j) => j.plan.id));
  const classics = plans.filter((p) => p.classic || !p.author);
  const friends = plans.filter((p) => !p.classic && p.author);

  return (
    <div className={ui.pageWide}>
      <header className={s.room}>
        <Art name="ruisdael" shape="none" decorative priority arrive focus="50% 88%" ratio="auto" className={s.roomArt} />
        <div className={s.glass}>
          <h1 className={s.title}>Plans</h1>
          <p className={s.lead}>Scripture a day at a time, alone or with friends. Everyone reads at their own pace.</p>
          <LinkButton to="/plans/new" variant="primary" icon="plus">
            Make a plan
          </LinkButton>
        </div>
      </header>

      {invitations.length > 0 && (
        <section className={s.block} aria-labelledby="inv-head">
          <SectionHeader id="inv-head" title="Invitations" />
          <ul className={`${s.invites} ${motionStyles.arrive}`}>
            {invitations.map((inv) => (
              <InvitationPlate key={inv.journeyId} inv={inv} />
            ))}
          </ul>
        </section>
      )}

      <section className={s.block} aria-labelledby="now-head">
        <SectionHeader id="now-head" title="Reading now" />
        {journeys.length === 0 ? (
          <EmptyState compact title="You’re not reading a plan yet." body="Start one from the shelf below, alone or with friends." />
        ) : (
          <ul className={`${s.cards} ${motionStyles.arrive}`}>
            {journeys.map((j) => (
              <JourneyCard key={j.id} j={j} />
            ))}
          </ul>
        )}
      </section>

      <section className={s.block} aria-labelledby="start-head">
        <SectionHeader id="start-head" title="Start a plan" description="From Bible Standard’s library of plans. Each opens with its days before you begin." />
        <Shelf plans={classics} reading={reading} />
      </section>

      <section className={s.block} aria-labelledby="friends-head">
        <SectionHeader id="friends-head" title="Friends’ plans" description="Plans your friends have made and shared, and your own." />
        <Shelf plans={friends} reading={reading} meId={session?.user.id} withMake />
      </section>

      <div className={s.map}>
        <BibleMap />
      </div>
    </div>
  );
}

/** Covers standing on a ledge, with the title and one line under each. */
function Shelf({ plans, reading, meId, withMake = false }: { plans: PlanSummary[]; reading: Set<string>; meId?: string; withMake?: boolean }) {
  return (
    <ul className={s.shelf}>
      {plans.map((p) => (
        <li key={p.id} className={s.spot}>
          <Link to="/plans/about/$planId" params={{ planId: p.id }} className={s.book}>
            <span className={s.stand}>
              <PlanCover plan={p} width={112} />
              {reading.has(p.id) && (
                <span className={s.reading}>
                  <Badge tone="solid">Reading</Badge>
                </span>
              )}
            </span>
            <span className={s.bookName}>{p.title}</span>
            <span className={s.bookLine}>
              {p.author ? (p.author.id === meId ? "Yours" : `By ${p.author.displayName}`) : `${p.days} days`}
              {p.companionWork?.author ? `, with ${p.companionWork.author}` : ""}
            </span>
          </Link>
        </li>
      ))}
      {withMake && (
        <li className={s.spot}>
          <Link to="/plans/new" className={s.book}>
            <span className={s.stand}>
              <span className={s.blank} aria-hidden="true">
                <Icon name="plus" size={22} />
              </span>
            </span>
            <span className={s.bookName}>Make your own plan</span>
            <span className={s.bookLine}>Choose the books and the pace.</span>
          </Link>
        </li>
      )}
    </ul>
  );
}

function InvitationPlate({ inv }: { inv: Invitation }) {
  const respond = useRespondInvitation();
  const navigate = useNavigate();
  const who = inv.from?.displayName ?? "Someone";
  const others = inv.members.filter((m) => m.id !== inv.from?.id).map((m) => first(m.displayName));
  return (
    <Plate as="li" pad="md" className={s.invite}>
      <PlanCover plan={inv.plan} width={64} />
      <div className={s.inviteWords}>
        <p className={s.inviteLine}>
          <Avatar user={inv.from ?? { displayName: who, avatarUrl: null }} size={28} />
          <span>
            <strong>{who}</strong> asked you to read <strong>{inv.plan.title}</strong> together.
          </span>
        </p>
        <p className={s.muted}>
          {inv.plan.scheduleKind === "calendar" ? `Read by date since ${fmt(inv.startedOn)}` : `${inv.plan.days} days, begun ${fmt(inv.startedOn)}`}
          {others.length > 0 && `, with ${names(others)}`}. You’d read at your own pace.
        </p>
        <div className={s.inviteActions}>
          <Button
            variant="primary"
            size="sm"
            disabled={respond.isPending}
            onClick={() =>
              respond.mutate({ journeyId: inv.journeyId, accept: true }, { onSuccess: (j) => j && void navigate({ to: "/plans/$journeyId", params: { journeyId: j.id } }) })
            }
          >
            Join
          </Button>
          <Button variant="ghost" size="sm" disabled={respond.isPending} onClick={() => respond.mutate({ journeyId: inv.journeyId, accept: false })}>
            Not now
          </Button>
        </div>
      </div>
    </Plate>
  );
}

/** One plan being read. The list carries the reader's place today (`me.day`, `me.today`), so no card fetches its schedule. */
function JourneyCard({ j }: { j: Journey }) {
  const others = othersIn(j);
  const readToday = others.filter((m) => m.read.includes(j.me.day));
  const day = j.me.today;
  const finished = new Set(j.me.read).size >= j.plan.days;
  const readMine = j.me.read.includes(j.me.day);

  let when = "";
  let line = "";
  if (finished) when = "Finished";
  else if (j.me.paused) when = "Paused";
  else if (localDate() < j.startedOn) when = `Starts ${fmt(j.startedOn)}`;
  else if (day) {
    when = j.follow === "byDate" ? day.heading : `Day ${j.me.day} of ${j.plan.days}`;
    line = readingsLine(day);
  }

  return (
    <Card as="li" interactive pad="none" className={s.card}>
      <Link to="/plans/$journeyId" params={{ journeyId: j.id }} className={s.cardLink}>
        <PlanCover plan={j.plan} width={72} />
        <span className={s.cardWords}>
          <span className={s.cardTitle}>{j.plan.title}</span>
          <span className={s.when}>
            {when}
            {readMine && !finished && !j.me.paused && (
              <span className={s.done}>
                <Icon name="check" size={14} />
                Read today
              </span>
            )}
          </span>
          {line && <span className={s.cardLine}>{line}</span>}
          {!finished && <TickBar read={new Set(j.me.read).size} total={j.plan.days} className={s.ticks} />}
          <span className={s.presence}>
            {j.together ? (
              <>
                {others.length > 0 && <AvatarStack users={others.map((m) => m.user)} max={4} size={22} />}
                {readToday.length > 0
                  ? `${names(readToday.map((m) => first(m.user.displayName)))} ${readToday.length === 1 ? "has" : "have"} read today`
                  : others.length > 0
                    ? `With ${names(others.map((m) => first(m.user.displayName)))}`
                    : j.invited.length > 0
                      ? `Waiting for ${names(j.invited.map((u) => first(u.displayName)))}`
                      : "Together, once someone joins"}
              </>
            ) : (
              "Reading alone"
            )}
          </span>
        </span>
        <span className={`${ui.goDisc} ${s.go}`} aria-hidden="true">
          <Icon name="go" size={16} />
        </span>
      </Link>
    </Card>
  );
}
